import React from "react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { ClaimColumnHeader } from "src/enums";
import { Claim, TableColumn } from "src/types";
import { StatusPill } from "src/components/ui";
import { Table } from "src/components";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";
import { Info } from "lucide-react";
import { useAuthorisation } from "src/context";
import { RoleHelper } from "src/helpers";

interface ClaimsDashTableProps {
  claims: Claim[];
  columnsToHide?: ClaimColumnHeader[];
}

export const ClaimsDashTable: React.FC<ClaimsDashTableProps> = ({ claims, columnsToHide }) => {
  const { userRole } = useAuthorisation();

  // Helper function to format date (MM/DD/YYYY)
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper function to calculate days open
  const calculateDaysOpen = (createdAt: string): number => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = now.getTime() - created.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper function to get color class based on days open
  const getDaysColorClass = (days: number): string => {
    if (days <= 7) return "text-green-600";
    if (days <= 14) return "text-yellow-600";
    if (days <= 21) return "text-orange-600";
    return "text-red-600";
  };

  // Helper function to check if status is active (not a final status)
  const isActiveStatus = (status: string): boolean => {
    const finalStatuses = ["Approved", "Rejected", "Partial", "Negotiated", "Canceled"];
    return !finalStatuses.includes(status);
  };

  // Helper function to get the highest urgency level from all claim details
  const getHighestUrgencyLevel = (claimDetails?: Array<{ urgencyLevel: string }>): string => {
    if (!claimDetails || claimDetails.length === 0) {
      return "-";
    }

    const urgencyLevels = claimDetails.map((detail) => detail.urgencyLevel);
    const urgencyPriority: Record<string, number> = {
      High: 3,
      Medium: 2,
      Low: 1
    };

    // Find the highest priority urgency level
    let highestUrgency = urgencyLevels[0];
    let highestPriority = urgencyPriority[urgencyLevels[0]] || 0;

    for (const urgency of urgencyLevels) {
      const priority = urgencyPriority[urgency] || 0;
      if (priority > highestPriority) {
        highestPriority = priority;
        highestUrgency = urgency;
      }
    }

    return highestUrgency || "-";
  };

  const claimsTableColumns: TableColumn<ClaimColumnHeader, Claim>[] = [
    {
      header: ClaimColumnHeader.ID,
      column: (row: Claim) => {
        const isActive = isActiveStatus(row.status);
        const daysOpen = isActive ? calculateDaysOpen(row.createdAt) : 0;
        const colorClass = isActive ? getDaysColorClass(daysOpen) : "";

        return (
          <Link
            to={`${ROUTES_CONFIG.claim.path}?id=${row.id}`}
            className={`hover:underline transition-colors font-mono text-sm ${colorClass || "text-primary-900 hover:text-primary-700"}`}
          >
            {row.id}
          </Link>
        );
      },
      styles: { minWidth: "180px" }
    },
    {
      header: ClaimColumnHeader.ISSUE_TYPE,
      column: (row: Claim) => <span className="capitalize">{row.issueType || "-"}</span>,
      styles: { textAlign: "center", minWidth: "110px" }
    },
    {
      header: ClaimColumnHeader.STATUS,
      column: (row: Claim) => {
        let linkPath: string | undefined;

        if (row.status === "Pending") {
          // Only visible to ops team
          if (RoleHelper.isHealfOperationsTeam(userRole)) {
            linkPath = `${ROUTES_CONFIG.editClaim.path}?id=${row.id}`;
          }
        } else if (row.status === "Under Review") {
          // Only visible to vendor and ops team
          if (userRole === "VENDOR" || RoleHelper.isHealfOperationsTeam(userRole)) {
            linkPath = `${ROUTES_CONFIG.reviewClaimConfirmation.path}?id=${row.id}`;
          }
        } else if (row.status === "Awaiting") {
          // Only visible to ops team
          if (RoleHelper.isHealfOperationsTeam(userRole)) {
            linkPath = `${ROUTES_CONFIG.claim.path}?id=${row.id}`;
          }
        }

        return (
          <span className="flex justify-center">
            <StatusPill status={row.status} linkPath={linkPath} />
          </span>
        );
      },
      styles: { textAlign: "center", minWidth: "110px" }
    },
    {
      header: ClaimColumnHeader.PURCHASE_ORDER_ID,
      column: (row: Claim) => <span className="font-mono text-sm">{row.purchaseOrderId || "-"}</span>,
      styles: { textAlign: "center", minWidth: "160px" }
    },
    {
      header: ClaimColumnHeader.VENDOR,
      column: (row: Claim) => row.vendorName || "-",
      styles: { textAlign: "center", minWidth: "150px" }
    },
    {
      header: ClaimColumnHeader.CLAIM_TYPE,
      column: (row: Claim) => {
        if (!row.claimDetails || row.claimDetails.length === 0) {
          return "-";
        }
        // Get all claim types and filter to unique values
        const claimTypes = row.claimDetails.map((detail) => detail.claimType).filter(Boolean);
        const uniqueClaimTypes = Array.from(new Set(claimTypes));
        return uniqueClaimTypes.length > 0 ? uniqueClaimTypes.join(", ") : "-";
      },
      styles: { textAlign: "center", minWidth: "180px" }
    },
    {
      header: ClaimColumnHeader.URGENCY_LEVEL,
      column: (row: Claim) => <span className="capitalize">{getHighestUrgencyLevel(row.claimDetails)}</span>,
      styles: { textAlign: "center", minWidth: "110px" }
    },
    {
      header: ClaimColumnHeader.WAREHOUSE_LOCATION,
      column: (row: Claim) => row.warehouseLocation,
      styles: { textAlign: "center", minWidth: "150px" }
    },
    {
      header: ClaimColumnHeader.SUBMITTED_BY,
      column: (row: Claim) => row.submittedByEmail || "-",
      styles: { textAlign: "center", minWidth: "180px" }
    },
    {
      header: ClaimColumnHeader.SENDER_EMAIL,
      column: (row: Claim) => row.senderEmail || "-",
      styles: { textAlign: "center", minWidth: "180px" }
    },
    {
      header: ClaimColumnHeader.CREATION_DATE,
      column: (row: Claim) => {
        const formattedDate = formatDate(row.createdAt);
        const isActive = isActiveStatus(row.status);

        if (isActive) {
          const daysOpen = calculateDaysOpen(row.createdAt);
          return (
            <span>
              {formattedDate}{" "}
              <span className="text-muted-600">
                ({daysOpen} {daysOpen === 1 ? "day" : "days"})
              </span>
            </span>
          );
        }

        return <span>{formattedDate}</span>;
      },
      styles: { textAlign: "center", minWidth: "220px" }
    },
    {
      header: ClaimColumnHeader.LAST_UPDATE,
      column: (row: Claim) => {
        if (!row.updatedAt) return <span>-</span>;
        return <span>{formatDate(row.updatedAt)}</span>;
      },
      styles: { textAlign: "center", minWidth: "130px" }
    },
    {
      header: ClaimColumnHeader.ACTIONS,
      column: (row: Claim) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to={`${ROUTES_CONFIG.claim.path}?id=${row.id}`}
                className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
              >
                <Info className="h-4 w-4 text-muted-600" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>
              <p>View Claim</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
      styles: { textAlign: "center", minWidth: "90px" }
    }
  ];

  return <Table data={claims} columns={claimsTableColumns} columnsToHide={columnsToHide} />;
};
